import React, {useEffect, useState} from "react";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import {Card} from "reactstrap";
import Grid from "../../../components/Grid";
import BarChart from "../../../components/BarChart";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useSearchParams} from "react-router-dom";
import {chargebackGridDefs} from "../../../utils/gridColumnDefs/storage/allUnits";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";

const groupOptions = [
    {value: "total_cost", label: "Total Cost ($)", route: "business_unit_chargeback_proc"},
    {value: "total_used", label: "Utilization", route: "business_unit_chargeback_proc"}
];

const reportOptions = [
    {
        label: 'Array Summary by Tag',
        route: "business_unit_chargeback_proc",
        value: 'business_unit'
    },
    {
        label: 'BU Summary by Array',
        route: "business_unit_chargeback_proc",
        value: 'device_name'
    }
];

export default function BUChargebackReport({context, title}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [chartSeries, setChartSeries] = useState([]);
    const [rowsToDisplay, setRowsToDisplay] = useState([]);
    const searchOption = searchParams.get("option");
    const defaultOption = searchOption ? groupOptions.find(({value}) => searchOption === value.toString()) : groupOptions[0];
    const [option, setOption] = useState(defaultOption);

    const searchReportOption = searchParams.get("report");
    const defaultReportOption = searchReportOption ? reportOptions.find(({value}) => searchReportOption === value.toString()) : reportOptions[0];
    const [reportOption, setReportOption] = useState(defaultReportOption);
    const [businessUnitList, setBusinessUnitList] = useState([]);
    const [deviceList, setDeviceList] = useState([]);
    const [gridData, setGridData] = useState([]);

    const { theme, client, periodId } = useVsiAppContext();

    useTitleFromComponent(`${context} / ${title}`);

    const [data, {isLoading}] = useDataWithRoute(`/api/v6/report/${reportOption.route}?client_id=${client.clientId}&period_id=${periodId}`);

    useEffect(() => {
        const chargebackData = (data?.recordsets?.[0] ?? []);
        const buList = data?.recordsets?.[1] ?? [];
        setGridData(data?.recordsets?.[3] ?? []);
        setBusinessUnitList(buList);
        const dList = data?.recordsets?.[2] ?? [];
        setDeviceList(dList);
        loadGraphWithoutGroup(chargebackData, reportOptions[0].value, option.value, buList, dList);
    }, [data]);

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if(node.data) filteredData.push(node.data);
        });

        loadGraphWithoutGroup(filteredData, reportOption.value, option.value, businessUnitList, deviceList);
    };

    const loadGraphWithoutGroup = (records, field, option_value, buList, dList) => {
        if (field === 'business_unit') {
            setCategories(buList.map(m => m.business_unit));
            let series = dList.map(device => ({
                name: device.device_name,
                data: buList.map(bu =>
                    parseFloat((records.reduce((totalAmount, record) =>
                        (record[field] === bu.business_unit && record['device_name'] === device.device_name) ? totalAmount + record[option_value] : totalAmount, 0)).toFixed(2)
                    )
                )
            }));
            setChartSeries(series);
        } else {
            setCategories(dList.map(m => m.device_name));
            let series = buList.map(bu => ({
                name: bu.business_unit,
                data: dList.map(device =>
                    parseFloat(records.reduce((totalAmount, record) =>
                        (record['business_unit'] === bu.business_unit && record[field] === device.device_name) ? totalAmount + record[option_value] : totalAmount, 0).toFixed(2)
                    )
                )
            }));
            setChartSeries(series);
        }
        setRowsToDisplay(records);
    };

    const onReportOptionChange = (report) => {
        setReportOption(report);
        searchParams.set("report", report.value);
        setSearchParams(searchParams);
        loadGraphWithoutGroup(rowsToDisplay, report.value, option.value, businessUnitList, deviceList);
    }

    const onOptionChange = (selectedOption) => {
        setOption(selectedOption);
        searchParams.set("option", selectedOption.value);
        setSearchParams(searchParams);
        loadGraphWithoutGroup(rowsToDisplay, reportOption.value, selectedOption.value, businessUnitList, deviceList);
    };

    const tooltip = {};
    if (option.value === "total_cost") {
        tooltip.valuePrefix = '$';
        tooltip.valueSuffix = null;
    } else if (option.value === "total_used") {
        tooltip.valueSuffix = ' GiB';
        tooltip.valuePrefix = null;
    }

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Chargeback Report</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 d-flex mb-2"}>
                    <VsiLabeledSelect key={`bar-chart-chargeback2`}
                                      isDisabled={isLoading}
                                      isLoading={isLoading}
                                      options={reportOptions}
                                      maxWidth={500}
                                      onChange={onReportOptionChange}
                                      defaultValue={defaultReportOption}/>
                    <VsiLabeledSelect key={`bar-chart-chargeback`}
                                      isDisabled={isLoading}
                                      isLoading={isLoading}
                                      options={groupOptions}
                                      onChange={onOptionChange}
                                      defaultValue={defaultOption}/>
                </div>
            </VsiBubble>
            <VsiBubble unit={defaultReportOption.value} autobotInfo={{ secondaryMetric: [defaultOption.value] }}>
                <BarChart categories={categories}
                          height={700}
                          yAxisLabels={`${option.value === 'total_cost' ? '${text}' : '{text}'}`}
                          series={chartSeries}
                          tooltip={tooltip}
                          scrollHeight={chartSeries?.[0]?.data?.length * 35 || null}
                          title={''}
                          yAxisTitle={option.label}/>
            </VsiBubble>
            <VsiBubble unit={defaultOption.value}>
                <Grid addStyle
                      data={gridData}
                      columnDefs={chargebackGridDefs}
                      saveParams={{unit: defaultOption.value}}
                      filterChanged={filterChanged}
                />
            </VsiBubble>
        </Card>
    );
}
